import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import Modal from '../../modals/Modal';
import ConnexionModule from '../ConnexionModule';
import Box from '../shared/atoms/Box';
import Text from '../shared/atoms/Text';
import Heading from '../shared/atoms/Heading';
import Button from '../shared/atoms/Button';
import Input from '../shared/atoms/Input';
import {
  openMessageThread,
  MAX_MESSAGE_LENGTH,
} from '../../services/messageThreads';
import { messageThreadActionErrorKey } from '../../utils/messageThreadStatus';

const ErrorBox = styled.div`
  padding: 14px;
  margin: 12px 0;
  background: ${(p) => p.theme.colors.red.pale};
  color: ${(p) => p.theme.colors.red.dark};
  border-radius: 8px;
  font-size: 14px;
`;

/**
 * FIXERLIGHT-049 — « Écrire au réparateur », le point d'entrée depuis la
 * fiche annuaire (contrat § 2, `origin = directory`). Exige d'être connecté,
 * comme la réservation : même mécanique que le compte du tunnel
 * (`annuaire/tunnel/EtapeCompte.jsx`) — `ConnexionModule` monté en ligne
 * avec `stayOnPage`, jamais un aller-retour vers `/login`. Une fois le
 * compte là, le composeur remplace le formulaire de connexion SANS fermer
 * la modale.
 */
export default function WriteToFixerModal({ open, onClose, shopId, shopName }) {
  const { t } = useTranslation();
  const history = useHistory();
  const clientId = useSelector((s) => (s.client && s.client.id) || null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setBody('');
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    setError(null);
    try {
      const thread = await openMessageThread(clientId, {
        shopId,
        body,
        origin: 'directory',
      });
      history.push(`/messages/${thread.id}`);
    } catch (e) {
      setError((e && e.kind) || 'generic');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onRequestClose={onClose} width="medium">
      <Heading level={5} mb={3}>
        {t('messages.write.title', { shop: shopName })}
      </Heading>
      {!clientId ? (
        <ConnexionModule
          noPadding
          bg="white"
          stayOnPage
          loginFirst
          closeModal={() => {}}
        />
      ) : (
        <Box data-testid="write-to-fixer-composer">
          <Input
            as="textarea"
            rows={4}
            name="write-to-fixer-body"
            bg="white"
            placeholder={t('messages.write.placeholder')}
            value={body}
            onChange={(e) =>
              setBody(e.target.value.slice(0, MAX_MESSAGE_LENGTH))
            }
            maxLength={MAX_MESSAGE_LENGTH}
            data-testid="write-to-fixer-input"
          />
          <Text
            fontSize={0}
            color="gray.dark"
            textAlign="right"
            mt={1}
            data-testid="write-to-fixer-counter">
            {body.length}/{MAX_MESSAGE_LENGTH}
          </Text>
          {error && (
            <ErrorBox data-testid="write-to-fixer-error">
              {t(messageThreadActionErrorKey(error))}
            </ErrorBox>
          )}
          <Button
            fullWidth
            mt={2}
            disabled={!body.trim() || sending}
            onClick={submit}
            data-testid="write-to-fixer-submit">
            {t('messages.write.submit')}
          </Button>
        </Box>
      )}
    </Modal>
  );
}
